import JSZip from 'jszip'
import type { FileModel } from '@/models/gallery.model'
import { getLogger } from '@/logger'

export class DownloadFetchError extends Error {
  readonly file: FileModel
  readonly status: number

  constructor(file: FileModel, status: number) {
    super(`Failed to fetch ${file.contentUrl} (${status})`)
    this.name = 'DownloadFetchError'
    this.file = file
    this.status = status
  }
}

/**
 * Fetches the given files, bundles them into a ZIP archive, and triggers a
 * browser download of `gallery-<galleryId>.zip`.
 *
 * @param files     - The files to include in the download.
 * @param galleryId - The gallery ID used to name the ZIP file.
 * @throws Re-throws any network or compression error after logging it.
 */
export async function downloadFiles(files: FileModel[], galleryId: string): Promise<void> {
  try {
    const zip = new JSZip()

    await Promise.all(
      files.map(async file => {
        const response = await fetch(file.contentUrl)
        if (!response.ok) {
          throw new DownloadFetchError(file, response.status)
        }
        const blob = await response.blob()
        zip.file(file.name, blob)
      }),
    )

    const zipBlob = await zip.generateAsync({ type: 'blob' })
    const url = URL.createObjectURL(zipBlob)

    const anchor = document.createElement('a')
    anchor.setAttribute('href', url)
    anchor.setAttribute('download', `gallery-${galleryId}.zip`)
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  } catch (err) {
    getLogger().error('downloadFiles failed', err)
    throw err
  }
}
